import { ChangeDetectionStrategy, Component, OnInit, Signal, computed, inject, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { Address } from '../../../core/models/address.model';
import { formatLineItemsSummary, Lead, LEAD_STAGES, parseLeadServices } from '../../../core/models/lead.model';
import { AddressService } from '../../../core/services/address.service';
import { LeadService } from '../../../core/services/lead.service';
import { TranslationService } from '../../../core/services/translation.service';
import { OrganizationUser, UserService } from '../../../core/services/user.service';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { CardActionsComponent } from '../../../shared/components/card/card-actions.component';
import { CardContentComponent } from '../../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../../shared/components/card/card-title.component';
import { CardComponent } from '../../../shared/components/card/card.component';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { LeadStageChipComponent } from '../../../shared/components/lead-stage-chip/lead-stage-chip.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { exportToCSV, exportToXLSX, generateFilename } from '../../../shared/utils/export.utils';

interface LeadFilterModel {
  stage: string;
  assigned_to: string;
}

/** Leads list/filter/export — design doc §12.2. */
@Component({
  selector: 'app-leads-list',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    ButtonComponent,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    CardActionsComponent,
    IconComponent,
    LeadStageChipComponent,
    TranslatePipe,
  ],
  templateUrl: './leads-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './leads-list.component.css',
})
export class LeadsListComponent implements OnInit {
  private leadService = inject(LeadService);
  private userService = inject(UserService);
  private addressService = inject(AddressService);
  private translate = inject(TranslationService);
  private router = inject(Router);

  leads!: Signal<Lead[]>;
  users!: Signal<OrganizationUser[]>;
  addresses!: Signal<Address[]>;

  userNames = computed(() => new Map(this.users().map((u) => [u.id, u.full_name || u.email])));
  addressLabels = computed(() => new Map(this.addresses().map((a) => [a.id, a.formatted_address])));

  filterForm = new FormGroup({});
  filterModel: LeadFilterModel = { stage: '', assigned_to: '' };
  filterFields: FormlyFieldConfig[] = [];

  /**
   * `computed()` only re-runs when a *signal* it reads changes — reading
   * `filterModel` (a plain object Formly mutates in place) directly here
   * would silently never re-filter. These signals, kept in sync via
   * `filterForm.valueChanges` below, are what make it reactive.
   */
  private stageFilter = signal('');
  private assignedToFilter = signal('');

  filteredLeads = computed(() => {
    const stage = this.stageFilter();
    const assignedTo = this.assignedToFilter();
    return this.leads().filter((lead) => (!stage || lead.stage === stage) && (!assignedTo || lead.assigned_to === assignedTo));
  });

  ngOnInit(): void {
    this.leads = this.leadService.createQuery().data;
    this.users = this.userService.createQuery(false).data;
    this.addresses = this.addressService.createQuery().data;

    this.filterForm.valueChanges.subscribe(() => {
      this.stageFilter.set(this.filterModel.stage);
      this.assignedToFilter.set(this.filterModel.assigned_to);
    });

    const t = (key: string) => this.translate.translate(key);
    this.filterFields = [
      {
        key: 'stage',
        type: 'native-select',
        props: {
          label: t('leads.list.filterStage'),
          showLabel: true,
          options: [
            { value: '', label: t('leads.list.allStages') },
            ...LEAD_STAGES.map((s) => ({ value: s, label: t(`leads.stage.${s}`) })),
          ],
        },
      },
      {
        key: 'assigned_to',
        type: 'native-select',
        props: {
          label: t('leads.list.filterAssignee'),
          showLabel: true,
          options: [
            { value: '', label: t('leads.list.everyone') },
            ...this.users().map((u) => ({ value: u.id, label: u.full_name || u.email })),
          ],
        },
      },
    ];
  }

  addressLabel(lead: Lead): string {
    return this.addressLabels().get(lead.address_id) ?? '';
  }

  assigneeName(lead: Lead): string {
    return this.userNames().get(lead.assigned_to) ?? '';
  }

  servicesSummary(lead: Lead): string {
    return formatLineItemsSummary(parseLeadServices(lead));
  }

  openLead(lead: Lead): void {
    this.router.navigate(['/leads', lead.id]);
  }

  private exportRows(): Record<string, string | number>[] {
    return this.filteredLeads().map((lead) => ({
      Contact: lead.contact_name,
      Phone: lead.contact_phone,
      Email: lead.contact_email,
      Address: this.addressLabel(lead),
      Stage: lead.stage,
      'Assigned to': this.assigneeName(lead),
      Services: this.servicesSummary(lead),
      'Estimated value': lead.estimated_value ?? '',
      Currency: lead.currency,
    }));
  }

  exportCsv(): void {
    exportToCSV(this.exportRows(), generateFilename('leads'));
  }

  exportXlsx(): void {
    exportToXLSX(this.exportRows(), generateFilename('leads'), 'Leads');
  }
}
